import { motion } from "framer-motion";
import type { ReactNode } from "react";

type Direction = "up" | "left" | "right" | "scale";

interface RevealProps {
  children: ReactNode;
  /** stagger delay in seconds */
  delay?: number;
  /** travel distance in px */
  distance?: number;
  direction?: Direction;
  className?: string;
  as?: "div" | "section" | "li" | "article" | "ul" | "ol";
}

const offset = (dir: Direction, d: number) => {
  switch (dir) {
    case "left":
      return { x: -d, y: 0 };
    case "right":
      return { x: d, y: 0 };
    case "scale":
      return { x: 0, y: 12 };
    default:
      return { x: 0, y: d };
  }
};

/**
 * Scroll-triggered reveal: fade + rise/slide + a touch of scale, once.
 * Respects prefers-reduced-motion automatically via framer-motion.
 */
export const Reveal = ({
  children,
  delay = 0,
  distance = 30,
  direction = "up",
  className,
  as = "div",
}: RevealProps) => {
  const MotionTag = motion[as];
  const o = offset(direction, distance);

  return (
    <MotionTag
      className={className}
      initial={{ opacity: 0, scale: 0.97, ...o }}
      whileInView={{ opacity: 1, scale: 1, x: 0, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.65, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </MotionTag>
  );
};
